// Standalone session transport: only the server decides correction, score and XP.
export const sessionCategories = ['all', 'movies', 'videogames', 'series', 'music', 'anime', 'comics', 'books'] as const;
export type SessionCategory = typeof sessionCategories[number];
export type SessionMode = 'demo' | 'solo';
export interface SessionSettings {
  category?: SessionCategory;
  difficulty?: 'easy' | 'medium' | 'hard' | 'mixed';
  questionCount?: 5 | 10 | 15 | 20;
  timePerQuestion?: 10 | 15 | 20 | 30;
}
export interface PublicQuestion {
  index: number; text: string; type: string; category: string; difficulty: string;
  options?: { id: string; text: string }[];
  imageUrl?: string; videoUrl?: string; audioUrl?: string;
  deadlineAt: number;
}
export interface SessionView {
  success: true; sessionId: string; expiresAt: number;
  phase: 'question' | 'correction' | 'finished'; questionCount: number; timePerQuestion: number;
  score: number; correctAnswers: number; answeredCount: number;
  question?: PublicQuestion;
  correction?: { index: number; correct: boolean; timedOut: boolean; answer: string; correctOptionId?: string; points: number; elapsedMs: number };
  persistence?: { status: 'pending' | 'saved' | 'error' | 'not_applicable'; gameId?: number; xpByUser?: Record<number, number> };
}
export class SessionError extends Error {
  constructor(public status: number, public code: string) { super(code); }
}
export interface SessionState { view: SessionView | null; busy: boolean; error: SessionError | null }
type Identity = { token: string; userId: number } | null;
type Operation = { path: string; method: 'POST' | 'GET'; body?: string };

export function secondsRemaining(deadlineAt: number, now = Date.now()) {
  return Math.max(0, Math.ceil((deadlineAt - now) / 1000));
}
export function confirmedXP(view: SessionView, userId: number) {
  return view.persistence?.status === 'saved' ? view.persistence.xpByUser?.[userId] ?? null : null;
}

function readView(value: any, mode: SessionMode): SessionView {
  const invalid = () => { throw new SessionError(502, 'invalid_session_payload'); };
  const number = (n: unknown) => typeof n === 'number' && Number.isFinite(n) && n >= 0;
  if (!value || value.success !== true || typeof value.sessionId !== 'string' || !value.sessionId ||
      !number(value.expiresAt) || !['question', 'correction', 'finished'].includes(value.phase) ||
      ![5, 10, 15, 20].includes(value.questionCount) || !number(value.timePerQuestion) ||
      !number(value.score) || !number(value.correctAnswers) || !number(value.answeredCount)) invalid();
  const result: SessionView = {
    success: true, sessionId: value.sessionId, expiresAt: value.expiresAt, phase: value.phase,
    questionCount: value.questionCount, timePerQuestion: value.timePerQuestion,
    score: value.score, correctAnswers: value.correctAnswers, answeredCount: value.answeredCount,
  };
  if (value.phase === 'question') {
    const q = value.question;
    if (!q || !Number.isInteger(q.index) || q.index < 0 || q.index >= value.questionCount ||
        typeof q.text !== 'string' || !q.text.trim() || !number(q.deadlineAt) || typeof q.type !== 'string') invalid();
    if (q.options !== undefined && (!Array.isArray(q.options) || q.options.length < 2 ||
        q.options.some((o: any) => !o || typeof o.id !== 'string' || !o.id || typeof o.text !== 'string' || !o.text.trim()) ||
        new Set(q.options.map((o: any) => o.id)).size !== q.options.length)) invalid();
    if ((mode === 'demo' || q.type === 'mcq') && !q.options) invalid();
    result.question = {
      index: q.index, text: q.text, type: q.type, category: q.category, difficulty: q.difficulty, deadlineAt: q.deadlineAt,
      ...(q.options ? { options: q.options.map((o: any) => ({ id: o.id, text: o.text })) } : {}),
      ...(mode === 'solo' ? Object.fromEntries(['imageUrl', 'videoUrl', 'audioUrl']
        .filter(key => typeof q[key] === 'string' && /^(https?:\/\/|\/(?!\/))/.test(q[key]))
        .map(key => [key, q[key]])) : {}),
    };
  } else {
    const c = value.correction;
    if (!c || !Number.isInteger(c.index) || c.index < 0 || c.index >= value.questionCount || typeof c.correct !== 'boolean' ||
        typeof c.timedOut !== 'boolean' || typeof c.answer !== 'string' || !number(c.points) || !number(c.elapsedMs)) invalid();
    result.correction = { index: c.index, correct: c.correct, timedOut: c.timedOut, answer: c.answer, points: c.points, elapsedMs: c.elapsedMs,
      ...(typeof c.correctOptionId === 'string' ? { correctOptionId: c.correctOptionId } : {}) };
  }
  if (mode === 'solo' && value.phase === 'finished') {
    const p = value.persistence;
    if (!p || !['pending', 'saved', 'error', 'not_applicable'].includes(p.status)) invalid();
    result.persistence = { status: p.status, ...(number(p.gameId) ? { gameId: p.gameId } : {}) };
    if (p.status === 'saved' && p.xpByUser && typeof p.xpByUser === 'object') {
      result.persistence.xpByUser = Object.fromEntries(Object.entries(p.xpByUser).filter((entry): entry is [string, number] => number(entry[1])));
    }
  }
  return result;
}

export class QuestionSession {
  state: SessionState = { view: null, busy: false, error: null };
  private listeners = new Set<() => void>();
  private pending: Operation | null = null;
  private owner: Identity = null;
  private revision = 0;
  private controller: AbortController | null = null;
  constructor(private mode: SessionMode, private identity: () => Identity, private fetcher: typeof fetch = (...args) => globalThis.fetch(...args)) {}
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getSnapshot = () => this.state;
  private publish(update: Partial<SessionState>) {
    this.state = { ...this.state, ...update };
    this.listeners.forEach(listener => listener());
  }
  reset(error: SessionError | null = null) {
    this.revision++; this.controller?.abort(); this.controller = null;
    this.pending = null; this.owner = null;
    this.publish({ view: null, busy: false, error });
  }
  private sameIdentity() {
    const current = this.identity();
    return this.mode === 'demo' || !!current && current.token === this.owner?.token && current.userId === this.owner?.userId;
  }
  private checkIdentity() {
    if (this.sameIdentity()) return true;
    this.reset(new SessionError(401, 'account_changed'));
    return false;
  }
  async start(settings: SessionSettings) {
    if (this.state.busy) return;
    this.reset(); this.owner = this.mode === 'solo' ? this.identity() : null;
    if (!this.checkIdentity()) return;
    const body = { category: settings.category ?? 'all', ...(this.mode === 'solo' ? {
      difficulty: settings.difficulty ?? 'mixed', questionCount: settings.questionCount ?? 10, timePerQuestion: settings.timePerQuestion ?? 20,
    } : {}) };
    await this.run({ path: '/start', method: 'POST', body: JSON.stringify(body) });
  }
  async answer(answer: string | null) {
    if (!this.checkIdentity() || this.state.busy || this.pending || this.state.view?.phase !== 'question') return;
    const q = this.state.view.question!;
    await this.run({ path: `/${encodeURIComponent(this.state.view.sessionId)}/answer`, method: 'POST',
      body: JSON.stringify({ index: q.index, ...(q.options ? { optionId: answer } : { text: answer }) }) });
  }
  async next() {
    if (!this.checkIdentity() || this.state.busy || this.pending || this.state.view?.phase !== 'correction') return;
    await this.run({ path: `/${encodeURIComponent(this.state.view.sessionId)}/next`, method: 'POST',
      body: JSON.stringify({ index: this.state.view.correction!.index + 1 }) });
  }
  async retry() {
    if (this.checkIdentity() && !this.state.busy && this.pending) await this.run(this.pending);
  }
  async refresh() {
    if (this.checkIdentity() && !this.state.busy && this.state.view) {
      await this.run({ path: `/${encodeURIComponent(this.state.view.sessionId)}`, method: 'GET' });
    }
  }
  async retrySave() {
    if (this.checkIdentity() && this.mode === 'solo' && !this.state.busy && this.state.view?.phase === 'finished' && this.state.view.persistence?.status !== 'saved') {
      await this.run({ path: `/${encodeURIComponent(this.state.view.sessionId)}/retry`, method: 'POST', body: '{}' });
    }
  }
  private async run(operation: Operation) {
    const revision = this.revision;
    this.pending = operation;
    this.publish({ busy: true, error: null });
    const controller = new AbortController(); this.controller = controller;
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (this.mode === 'solo') headers.Authorization = `Bearer ${this.owner!.token}`;
      const response = await this.fetcher(`/api/${this.mode}${operation.path}`, { method: operation.method, headers, body: operation.body, signal: controller.signal, cache: 'no-store' });
      const raw = await response.text();
      let data: any;
      try { data = JSON.parse(raw); } catch { throw new SessionError(response.ok ? 502 : response.status, 'unreadable_response'); }
      if (!response.ok || data?.success !== true) throw new SessionError(response.status, data?.error || 'request_failed');
      const view = readView(data, this.mode);
      if (revision !== this.revision || !this.checkIdentity()) return;
      this.pending = null;
      this.publish({ view, busy: false, error: null });
    } catch (error) {
      if (revision !== this.revision || !this.checkIdentity()) return;
      this.publish({ busy: false, error: error instanceof SessionError ? error : new SessionError(0, 'network_error') });
    } finally { clearTimeout(timeout); }
  }
}

export function sessionErrorText(error: SessionError) {
  if (error.status === 401) return 'Votre connexion a changé ou a expiré. Connectez-vous pour commencer une nouvelle partie.';
  if (error.status === 410 || error.status === 404) return 'Cette partie a expiré ou n’est plus disponible. Vous pouvez recommencer.';
  if (error.status === 429) return 'Trop de demandes. Patientez quelques instants puis réessayez.';
  if (error.code === 'not_enough_questions') return 'Pas assez de questions disponibles pour ces réglages. Choisissez une autre catégorie ou moins de questions.';
  if (error.status === 502) return 'La question reçue est incomplète ou illisible. Réessayez ou recommencez.';
  if (error.status === 409) return 'La partie a déjà avancé. Récupérez son état pour continuer.';
  if (error.status === 503) return 'Le service est momentanément indisponible. Réessayez dans quelques instants.';
  return 'La réponse du serveur n’a pas pu être confirmée. Réessayez : votre choix initial sera conservé.';
}
